/**
 * groupEvents — Pure function: gruppiert LeadEvents nach Tag-Label.
 *
 * Verwendet in `TimelineTab.tsx` (Aktivitaets-Tab des Card-Detail-Modal,
 * Plan 03-02, CARD-02).
 *
 * Logik:
 *  - Sortiere Events nach `createdAt` DESC (neueste zuerst).
 *  - Berechne pro Event ein Tag-Label relativ zu `now`:
 *      0 Tage Differenz  → 'Heute'
 *      1 Tag             → 'Gestern'
 *      2-6 Tage          → 'vor X Tagen'
 *      >= 7 Tage         → 'dd.MM.yyyy' (de-locale)
 *  - Reihenfolge der Buckets folgt der Reihenfolge des ersten Events
 *    (neuestes zuerst), damit Heute > Gestern > vor X Tagen > Datums-Buckets
 *    automatisch eingehalten wird.
 *  - Innerhalb eines Buckets bleibt die Sortierung neuestes Event zuerst.
 *
 * `now` ist Argument → Tests koennen die Referenz-Zeit fixieren.
 */

import { differenceInCalendarDays } from 'date-fns';
import type { LeadEvent } from '../types';

export interface EventBucket {
  label: string;
  events: LeadEvent[];
}

function formatGermanDate(d: Date): string {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

export function labelFor(eventDate: Date, now: Date): string {
  const days = differenceInCalendarDays(now, eventDate);
  if (days <= 0) return 'Heute';
  if (days === 1) return 'Gestern';
  if (days >= 2 && days <= 6) return `vor ${days} Tagen`;
  return formatGermanDate(eventDate);
}

export function groupEventsByDay(
  events: LeadEvent[],
  now: Date = new Date(),
): EventBucket[] {
  if (events.length === 0) return [];

  const sorted = [...events].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );

  const buckets: EventBucket[] = [];
  const indexByLabel = new Map<string, number>();

  for (const ev of sorted) {
    const label = labelFor(new Date(ev.createdAt), now);
    const existing = indexByLabel.get(label);
    if (existing != null) {
      buckets[existing].events.push(ev);
    } else {
      indexByLabel.set(label, buckets.length);
      buckets.push({ label, events: [ev] });
    }
  }

  return buckets;
}
